import { RefObject, useEffect } from 'react'

interface UseScrollToSelectedItemOptions {
  /** 延迟执行时间（毫秒），确保 DOM 已渲染 */
  delay?: number
  /** 滚动行为 */
  behavior?: 'auto' | 'smooth'
  /** 滚动位置 */
  block?: 'start' | 'center' | 'end' | 'nearest'
  inline?: 'start' | 'center' | 'end' | 'nearest'
  /** 是否禁用滚动（例如在自动滚动动画执行期间） */
  disabled?: boolean
}

/**
 * 选中项滚动到视窗 Hook
 * - 当 selectedItemId 变化时，自动将对应的元素滚动到视窗中心
 */
const useScrollToSelectedItem = <T extends HTMLElement>(
  containerRef: RefObject<T>,
  selectedItemId: string | number | null | undefined,
  options: UseScrollToSelectedItemOptions = {}
) => {
  const {
    delay = 100,
    behavior = 'smooth',
    block = 'nearest',
    inline = 'center',
    disabled = false
  } = options

  useEffect(() => {
    if (!selectedItemId || !containerRef.current || disabled) {
      return
    }

    // 使用 setTimeout 确保 DOM 已渲染
    const timer = setTimeout(() => {
      const container = containerRef.current
      if (!container) return

      // 通过 data 属性查找选中的商品项
      const selectedItem = container.querySelector(
        `[data-goods-id="${selectedItemId}"]`
      ) as HTMLElement

      if (selectedItem) {
        // 使用 scrollIntoView 方法，更简单可靠
        selectedItem.scrollIntoView({
          behavior,
          block,
          inline
        })
      }
    }, delay)

    return () => clearTimeout(timer)
  }, [selectedItemId, containerRef, delay, behavior, block, inline, disabled])
}

export default useScrollToSelectedItem
